import { Link } from 'react-router-dom';
import { MONITOR_MODE_DEFS } from '@goyone/shared';
import { useAuth } from '../../auth/AuthProvider';
import { Icon } from '../../components/Icon';
import { useScreenMonitor } from '../../components/ScreenMonitor';
import { Badge, Notice, PageHead } from '../../components/ui';

/**
 * إذنُ مراقبة الشاشة.
 *
 * كان زرًّا في الشريط العلوي، والإذنُ ليس عملًا يوميًّا يُضغط بين المهامّ:
 * هو موافقةٌ يمنحها الموظّف للموقع على مشاركة شاشته، ومكانُها الإعدادات
 * حيث يُقرأ ما يُلتقط ولماذا قبل الموافقة.
 *
 * ## والإذنُ لا يُحفظ — وهذا من المتصفّح لا منّا
 *
 * المتصفّحات لا تسمح لموقعٍ بحفظ إذن مشاركة الشاشة ولا ببدئها دون ضغطةٍ
 * من صاحبها في كلّ جلسة. فالصفحةُ تقول ذلك صراحةً بدل أن توهم بإذنٍ دائم
 * يسقط بأوّل إعادة تحميل — وتدلّ على ما يحفظه: تطبيقُ سطح المكتب
 * (`apps/desktop`)، حيث يُمنح الإذنُ مرّةً على الجهاز وتبدأ المشاركةُ وحدها.
 */
export function ScreenPermission() {
  const { atLeast } = useAuth();
  const { policy, inDesktop, active, external, start, stop } = useScreenMonitor();

  const mode = policy ? MONITOR_MODE_DEFS.find((d) => d.key === policy.mode) : null;
  const required = policy !== null && policy.mode !== 'OFF';

  return (
    <div className="page">
      <PageHead
        title="إذن مراقبة الشاشة"
        subtitle="مشاركةُ شاشتك مع الإدارة كما تطلبها سياسةُ الشركة"
      />

      {!policy && <div className="skeleton" style={{ height: 160 }} />}

      {policy && !required && (
        <Notice kind="info" title="شركتك لا تطلب مراقبة الشاشة الآن">
          لا يُطلب منك منحُ أيّ إذن. إن غيّرت الإدارةُ السياسة ظهر هنا ما تطلبه.
          {atLeast('ADMIN') && (
            <>
              {' '}
              <Link to="/settings/monitoring">إعدادات المراقبة</Link>
            </>
          )}
        </Notice>
      )}

      {required && (
        <div className="card" style={{ maxWidth: 720 }}>
          <div className="card-head">
            <h2 className="card-title">مشاركة الشاشة</h2>
            <span className="row" style={{ marginInlineStart: 'auto', gap: 'var(--sp-2)' }}>
              {inDesktop && <Badge kind="info">تطبيق سطح المكتب</Badge>}
              <Badge kind={active ? 'danger' : 'neutral'}>
                {active ? 'مفعّلة الآن' : 'غير مفعّلة'}
              </Badge>
            </span>
          </div>

          <div className="card-body">
            <div className="col" style={{ gap: 'var(--sp-3)' }}>
              <p className="m-0">
                <span className="strong">ما تطلبه الشركة: </span>
                {mode?.label}
                {policy.mode === 'SCREENSHOTS' &&
                  ` — لقطةٌ كلَّ ${policy.screenshotEverySec} ثانية`}
                .
              </p>

              {inDesktop ? (
                <ul
                  className="col"
                  style={{ gap: 'var(--sp-2)', margin: 0, paddingInlineStart: 20 }}
                >
                  <li>
                    الإذنُ يُمنح مرّةً واحدةً على هذا الجهاز: أوّلَ مرّةٍ يسألك التطبيقُ موافقتَك،
                    وبعدها تبدأ المشاركةُ وحدها كلَّما فتحت التطبيق وسجّلت دخولك.
                  </li>
                  <li>
                    يُشارَك <strong>نافذةُ GoyOne وحدَها</strong> بلا نافذة اختيار — لا سطحُ مكتبك
                    ولا تطبيقاتُك الأخرى. ولا تُؤخذ لقطةٌ حين تكون في تطبيقٍ آخر خارج النظام.
                  </li>
                  <li>
                    أيقونةُ GoyOne بجوار الساعة تقول إن كانت المراقبةُ فعّالة، ومنها تسحب موافقتك
                    فتتوقّف فورًا.
                  </li>
                  <li>إيقافُها من هنا يسري حتى تعيد فتح التطبيق.</li>
                </ul>
              ) : (
                <>
                  <ul
                    className="col"
                    style={{ gap: 'var(--sp-2)', margin: 0, paddingInlineStart: 20 }}
                  >
                    <li>
                      حين تضغط «منح الإذن» يسألك المتصفّح أيَّ شاشةٍ تشارك — والمشاركةُ لا تبدأ إلّا
                      باختيارك.
                    </li>
                    <li>
                      تبقى المشاركةُ قائمةً وأنت تتنقّل بين أقسام النظام، وكلُّ لقطةٍ تُحفظ باسم
                      الشاشة التي كنت عليها.
                    </li>
                    <li>
                      في المتصفّح يُطلب الإذنُ من جديد كلَّما أعدت تحميل الصفحة أو فتحت النظام:
                      المتصفّحات لا تسمح لأيّ موقعٍ بحفظ إذن مشاركة الشاشة.
                    </li>
                  </ul>

                  <Notice kind="info" icon="video" title="إذنٌ مرّةً واحدة على الجهاز">
                    ثبّت <strong>تطبيق GoyOne لسطح المكتب</strong>: توافق فيه مرّةً واحدة، وتبدأ
                    المشاركةُ وحدها كلَّما فتحته — بلا نافذة اختيارٍ كلَّ مرّة. اطلب ملفَّ تثبيته من
                    مدير النظام.
                  </Notice>
                </>
              )}

              {external && active && (
                <Notice kind="info" title="الجلسة يملكها كونسول المكالمات">
                  بدأت المشاركةُ من الكونسول مع ورديّتك. إيقافُها من هنا يُنهي ورديّة الكونسول
                  أيضًا.
                </Notice>
              )}

              <div className="row" style={{ gap: 'var(--sp-2)' }}>
                {active ? (
                  <button className="btn btn-danger monitor-live" onClick={stop}>
                    <span className="cc-rec-dot" />
                    إيقاف المشاركة
                  </button>
                ) : (
                  <button className="btn btn-primary" onClick={() => void start()}>
                    <Icon name="video" size={16} />
                    منح الإذن وبدء المشاركة
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
